import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { sendContractTransaction, approveToken, formatTransactionError } from '../utils/transactionHelper';
import './Staking.css';

const Staking = () => {
  const web3Context = useWeb3();
  const { account, contract, web3, usdtContract, CONTRACT_ADDRESS, USDT_ADDRESS } = web3Context;

  const [activeTab, setActiveTab] = useState('stake');
  const [stakeAmount, setStakeAmount] = useState('');
  const [stakes, setStakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [stakingEnabled, setStakingEnabled] = useState(true);
  const [tokenPrice, setTokenPrice] = useState('1');
  const [claimDelay, setClaimDelay] = useState(0);
  const [stakeDuration, setStakeDuration] = useState(0);

  useEffect(() => {
    if (account && contract && usdtContract) {
      loadStakes();
      loadBalances();
      loadStakingInfo();
    }
  }, [account, contract, usdtContract]);

  const loadBalances = async () => {
    try {
      const balance = await usdtContract.methods.balanceOf(account).call();
      setUsdtBalance(web3.utils.fromWei(balance, 'ether'));
    } catch (error) {
      console.error('Error loading USDT balance:', error);
    }
  };

  const loadStakingInfo = async () => {
    try {
      const [enabled, price, delay, duration] = await Promise.all([
        contract.methods.stakingEnabled().call(),
        contract.methods.tokenPerUSDT().call(),
        contract.methods.claimDelay().call(),
        contract.methods.stakeDuration().call()
      ]);

      setStakingEnabled(enabled);
      setTokenPrice(price.toString());
      setClaimDelay(parseInt(delay));
      setStakeDuration(parseInt(duration));
    } catch (error) {
      console.error('Error loading staking parameters:', error);
    }
  };

  const loadStakes = async () => {
    try {
      const stakeCount = await contract.methods.getStakeCount(account).call();
      const userStakes = [];

      for (let i = 0; i < parseInt(stakeCount); i++) {
        const stake = await contract.methods.getStake(account, i).call();
        const currentTime = Math.floor(Date.now() / 1000);
        const unlockTime = parseInt(stake.startTime) + claimDelay;
        const endTime = parseInt(stake.startTime) + stakeDuration;
        const elapsed = Math.min(currentTime - parseInt(stake.startTime), stakeDuration);

        // Linear unlock formula matching contract: unlocked = (totalReward * elapsed) / stakeDuration
        const unlockedWei = elapsed > 0 ? (window.BigInt(stake.totalReward) * window.BigInt(elapsed)) / window.BigInt(stakeDuration || 1) : 0n;
        const claimedWei = window.BigInt(stake.claimedReward);
        const claimableWei = unlockedWei > claimedWei ? unlockedWei - claimedWei : 0n;

        userStakes.push({
          index: i,
          amount: web3.utils.fromWei(stake.amount.toString(), 'ether'),
          totalReward: web3.utils.fromWei(stake.totalReward.toString(), 'ether'),
          claimedReward: web3.utils.fromWei(stake.claimedReward.toString(), 'ether'),
          claimable: web3.utils.fromWei(claimableWei.toString(), 'ether'),
          startTime: parseInt(stake.startTime),
          unlockTime,
          endTime,
          active: stake.active,
          progress: Math.min((elapsed / (stakeDuration || 1)) * 100, 100),
          canClaim: currentTime >= unlockTime && claimableWei > 0n && stake.active
        });
      }

      setStakes(userStakes);
    } catch (error) {
      console.error('Error loading user stakes:', error);
    }
  };

  const handleStake = async () => {
    if (!stakingEnabled) {
      alert('Staking program is currently disabled by admin.');
      return;
    }

    const amtNum = parseFloat(stakeAmount);
    if (isNaN(amtNum) || amtNum <= 0) {
      alert('Please enter a valid positive USDT amount');
      return;
    }

    if (amtNum > parseFloat(usdtBalance)) {
      alert(`Insufficient USDT balance! You have ${usdtBalance} USDT.`);
      return;
    }

    setLoading(true);
    try {
      const amountWei = web3.utils.toWei(stakeAmount, 'ether');

      // Step 1: Approve USDT transfer
      await approveToken(web3Context, USDT_ADDRESS, CONTRACT_ADDRESS, amountWei);

      // Step 2: Send stake transaction
      const method = contract.methods.stake(amountWei);
      await sendContractTransaction(web3Context, method);

      alert(`Successfully staked ${stakeAmount} USDT! Payout target: ${(amtNum * 3).toLocaleString()} USDT (3x Target).`);
      setStakeAmount('');
      loadStakes();
      loadBalances();
    } catch (error) {
      console.error('Error staking:', error);
      alert('Staking failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (stakeIndex) => {
    setLoading(true);
    try {
      const method = contract.methods.claimStake(stakeIndex);
      await sendContractTransaction(web3Context, method);

      alert('Stake reward claimed successfully! Token rewards transferred to your wallet.');
      loadStakes();
      loadBalances();
    } catch (error) {
      console.error('Error claiming stake:', error);
      alert('Claim failed: ' + formatTransactionError(error));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    if (seconds <= 0) return 'Unlocked';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return `${days}d ${hours}h`;
  };

  const setPercentage = (pct) => {
    const maxVal = parseFloat(usdtBalance);
    const calculated = (maxVal * (pct / 100)).toFixed(4);
    setStakeAmount(calculated);
  };

  return (
    <div className="staking-wrapper">
      {/* Header Banner */}
      <div className="staking-banner-card">
        <div className="banner-text">
          <h2>💎 USDT Staking Vault</h2>
          <p>Earn 3x fixed total return on your USDT deposits with linear token payouts.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-box">
            <span className="stat-label">Vault Status</span>
            <span className={`stat-status ${stakingEnabled ? 'enabled' : 'disabled'}`}>
              {stakingEnabled ? '● ACTIVE' : '○ DISABLED'}
            </span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Return Multiplier</span>
            <span className="stat-val multiplier">3x Target</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">Cliff Delay</span>
            <span className="stat-val">{formatDuration(claimDelay)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="staking-tabs">
        <button
          className={`tab-btn ${activeTab === 'stake' ? 'active' : ''}`}
          onClick={() => setActiveTab('stake')}
        >
          ➕ Stake USDT
        </button>
        <button
          className={`tab-btn ${activeTab === 'mystakes' ? 'active' : ''}`}
          onClick={() => setActiveTab('mystakes')}
        >
          📊 My Active Vaults ({stakes.length})
        </button>
      </div>

      {activeTab === 'stake' && (
        <div className="stake-panel">
          <div className="balance-bar">
            <span>Available Wallet Balance:</span>
            <strong className="usdt-amount">{parseFloat(usdtBalance).toLocaleString()} USDT</strong>
          </div>

          <div className="input-group">
            <label>Deposit Amount (USDT):</label>
            <div className="input-field-wrapper">
              <input
                type="number"
                placeholder="0.00"
                value={stakeAmount}
                onChange={(e) => setStakeAmount(e.target.value)}
                disabled={loading || !stakingEnabled}
              />
              <button
                className="max-btn"
                onClick={() => setStakeAmount(usdtBalance)}
                disabled={loading || !stakingEnabled}
              >
                MAX
              </button>
            </div>

            <div className="pct-grid">
              <button onClick={() => setPercentage(25)} disabled={loading || !stakingEnabled}>25%</button>
              <button onClick={() => setPercentage(50)} disabled={loading || !stakingEnabled}>50%</button>
              <button onClick={() => setPercentage(75)} disabled={loading || !stakingEnabled}>75%</button>
              <button onClick={() => setPercentage(100)} disabled={loading || !stakingEnabled}>100%</button>
            </div>
          </div>

          {/* Staking Calculator Preview */}
          {stakeAmount && parseFloat(stakeAmount) > 0 && (
            <div className="preview-card">
              <h4>Vault Projection & Payout Schedule</h4>
              <div className="preview-row">
                <span>USDT Deposit:</span>
                <span>{parseFloat(stakeAmount).toLocaleString()} USDT</span>
              </div>
              <div className="preview-row">
                <span>Total 3x Payout Target:</span>
                <span className="reward-highlight">{(parseFloat(stakeAmount) * 3).toLocaleString()} USDT</span>
              </div>
              <div className="preview-row">
                <span>Token Reward Value:</span>
                <span>{((parseFloat(stakeAmount) * 3) * (parseFloat(tokenPrice) || 1)).toLocaleString()} Tokens</span>
              </div>
              <div className="preview-row">
                <span>Initial Claim Lock (Cliff):</span>
                <span>{formatDuration(claimDelay)}</span>
              </div>
              <div className="preview-row">
                <span>Total Vesting Duration:</span>
                <span>{formatDuration(stakeDuration)}</span>
              </div>
            </div>
          )}

          <button
            className="btn-execute-stake"
            onClick={handleStake}
            disabled={loading || !stakeAmount || parseFloat(stakeAmount) <= 0 || !stakingEnabled}
          >
            {loading ? 'Processing Approval & Deposit...' : 'Approve USDT & Deposit to Vault'}
          </button>
        </div>
      )}

      {activeTab === 'mystakes' && (
        <div className="stakes-grid">
          {stakes.length === 0 ? (
            <div className="empty-stakes">
              <div className="empty-icon">💎</div>
              <h4>No Active Stakes Found</h4>
              <p>Deposit USDT in the "Stake USDT" tab to begin earning 3x linear rewards.</p>
            </div>
          ) : (
            stakes.map((stk) => (
              <div key={stk.index} className="vault-card">
                <div className="vault-header">
                  <div className="vault-id">
                    <span>Vault #{stk.index + 1}</span>
                    <span className={`status-tag ${stk.active ? 'active' : 'completed'}`}>
                      {stk.active ? 'Linear Vesting' : 'Matured'}
                    </span>
                  </div>
                  <div className="vault-deposit">{parseFloat(stk.amount).toLocaleString()} USDT</div>
                </div>

                <div className="vault-body">
                  <div className="v-row">
                    <span>Target 3x Return:</span>
                    <span className="reward-txt">{parseFloat(stk.totalReward).toLocaleString()} USDT</span>
                  </div>
                  <div className="v-row">
                    <span>Claimed Rewards:</span>
                    <span>{parseFloat(stk.claimedReward).toLocaleString()} USDT</span>
                  </div>
                  <div className="v-row">
                    <span>Claimable Now:</span>
                    <span className="claimable-txt">{parseFloat(stk.claimable).toLocaleString()} USDT</span>
                  </div>
                  <div className="v-row">
                    <span>Start Date:</span>
                    <span>{formatTime(stk.startTime)}</span>
                  </div>
                  <div className="v-row">
                    <span>Unlock Date:</span>
                    <span>{formatTime(stk.unlockTime)}</span>
                  </div>

                  {/* Meter Bar */}
                  <div className="progress-meter-wrapper">
                    <div className="meter-bar">
                      <div className="meter-fill" style={{ width: `${stk.progress}%` }}></div>
                    </div>
                    <div className="meter-info">
                      <span>Vesting Progress</span>
                      <span>{stk.progress.toFixed(1)}%</span>
                    </div>
                  </div>

                  {stk.canClaim && (
                    <button
                      className="btn-claim-reward"
                      onClick={() => handleClaim(stk.index)}
                      disabled={loading}
                    >
                      {loading ? 'Claiming...' : `Claim ${stk.claimable} USDT in Tokens`}
                    </button>
                  )}

                  {!stk.canClaim && stk.active && (
                    <div className="cliff-notice">
                      {Math.floor(Date.now() / 1000) < stk.unlockTime
                        ? `⏳ Cliff Lock Active: Unlocks in ${formatDuration(stk.unlockTime - Math.floor(Date.now() / 1000))}`
                        : '✓ Vesting ongoing, check back as rewards unlock.'}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Staking;